/**
 * reactionDetailsModel — WHO reacted, and who read it, as data (2026-09-19).
 *
 * Reactions shipped with the names of the people behind a count living in ONE
 * place: the chip's `title` attribute. A touch browser never renders `title`,
 * so on a phone — Miles's own report — there was no way at all to find out who
 * reacted, and on a desktop the only way was a second-long hover producing an
 * unstyled OS tooltip. SeenByMarks had the identical hole two elements along
 * the same meta line.
 *
 * The data was never missing: `summarizeReactions` already returns every uid
 * per emoji and the host already resolves uid → display name. This module is
 * the arrangement that presentation was lacking — ONE model read by all three
 * surfaces (the phone sheet, the desktop panel, the hover card), so "who
 * reacted" can never mean three different things depending on where you look.
 *
 * A pure, import-free leaf by design (the headerRailTiers / composerEnterPolicy
 * idiom): the chat-isolation suites render its consumers, and a module that
 * reaches `firebase/auth` — even transitively, even for a one-line helper —
 * takes those suites down at import time before a test body runs.
 *
 * PRIVACY (R8, CLAUDE.md): a person is a NAME and an initial here. Never a
 * role, never an email. The uids were already in every participant's browser;
 * this adds a way to read them, never a new disclosure.
 */
import { senderGlyph } from '../groupThreads/senderGlyph';
import type { ReactionSummaryEntry } from './reactionModel';

/** Structurally the SeenByReader the host already builds — declared, not imported, to keep this leaf free of component modules. */
export interface ActivityReader {
  id: string;
  label: string;
  glyph: string;
  /** Prest0n's receipt renders with the assistant mark. */
  assistant?: boolean;
}

export interface ActivityPerson {
  uid: string;
  label: string;
  glyph: string;
  /** Every emoji this person gave, in the order the chips carry them. Empty on the `seen` tab. */
  emojis: string[];
  isSelf: boolean;
  assistant?: boolean;
}

export type ActivityTabKind = 'all' | 'emoji' | 'seen';

export interface ActivityTab {
  key: string;
  kind: ActivityTabKind;
  /** Present on an `emoji` tab only. */
  emoji?: string;
  count: number;
  people: ActivityPerson[];
}

export interface MessageActivity {
  tabs: ActivityTab[];
  /** Distinct people who reacted (never counting anyone twice for two emoji). */
  reactorCount: number;
}

export const ALL_TAB_KEY = 'all';
export const SEEN_TAB_KEY = 'seen';
export const emojiTabKey = (emoji: string): string => `emoji:${emoji}`;

/** The hover card names at most this many people before "+N more" — the server's own REACTION_NAMES_SHOWN. */
export const NAMES_PREVIEW_MAX = 3;

/**
 * You first, then by label. The store keeps no per-reaction timestamp, so
 * there is no honest "most recent" to sort by and none is invented.
 */
function orderPeople(a: ActivityPerson, b: ActivityPerson): number {
  if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1;
  return a.label.localeCompare(b.label);
}

/**
 * The tabs a message's activity surface shows.
 *
 * `all` appears only when there are two or more emoji: with one emoji it would
 * be a byte-identical second copy of the tab beside it.
 */
export function buildMessageActivity(input: {
  summary: ReadonlyArray<ReactionSummaryEntry>;
  readers?: ReadonlyArray<ActivityReader>;
  selfUid: string | null | undefined;
  labelFor: (uid: string) => string;
}): MessageActivity {
  const self = (input.selfUid ?? '').trim();
  const summary = input.summary ?? [];
  const readers = input.readers ?? [];

  // uid → the person, accumulating every emoji they gave.
  const byUid = new Map<string, ActivityPerson>();
  const order: string[] = [];
  for (const entry of summary) {
    for (const uid of entry.uids) {
      let person = byUid.get(uid);
      if (!person) {
        const label = input.labelFor(uid) || '';
        person = { uid, label, glyph: senderGlyph(label), emojis: [], isSelf: !!self && uid === self };
        byUid.set(uid, person);
        order.push(uid);
      }
      if (!person.emojis.includes(entry.emoji)) person.emojis.push(entry.emoji);
    }
  }
  const everyone = order.map((uid) => byUid.get(uid)!).sort(orderPeople);

  const tabs: ActivityTab[] = [];
  if (summary.length > 1) {
    tabs.push({ key: ALL_TAB_KEY, kind: 'all', count: everyone.length, people: everyone });
  }
  for (const entry of summary) {
    const people = entry.uids
      .map((uid) => byUid.get(uid))
      .filter((p): p is ActivityPerson => !!p)
      .map((p) => ({ ...p, emojis: [entry.emoji] }))
      .sort(orderPeople);
    tabs.push({ key: emojiTabKey(entry.emoji), kind: 'emoji', emoji: entry.emoji, count: people.length, people });
  }
  if (readers.length > 0) {
    const people: ActivityPerson[] = readers.map((r) => ({
      uid: r.id,
      label: r.label,
      glyph: r.glyph || senderGlyph(r.label),
      emojis: [],
      isSelf: !!self && r.id === self,
      assistant: r.assistant,
    }));
    tabs.push({ key: SEEN_TAB_KEY, kind: 'seen', count: people.length, people });
  }

  return { tabs, reactorCount: everyone.length };
}

/** The tab to open on: the caller's preference when it exists, else the first. */
export function resolveActiveTab(tabs: ReadonlyArray<ActivityTab>, preferred?: string | null): string | null {
  if (tabs.length === 0) return null;
  if (preferred && tabs.some((t) => t.key === preferred)) return preferred;
  return tabs[0].key;
}

/** One line of names for the hover card: you first, then by label, truncated with a count. */
export function reactionNamesPreview(
  entry: ReactionSummaryEntry,
  labelFor: (uid: string) => string,
  lang: 'en' | 'es',
  selfUid?: string | null,
  max: number = NAMES_PREVIEW_MAX,
): string {
  const self = (selfUid ?? '').trim();
  const names = entry.uids
    .map((uid) => ({ uid, label: labelFor(uid) || '', isSelf: !!self && uid === self }))
    .filter((p) => !!p.label)
    .sort((a, b) => (a.isSelf !== b.isSelf ? (a.isSelf ? -1 : 1) : a.label.localeCompare(b.label)))
    .map((p) => p.label);
  if (names.length === 0) return '';
  if (names.length <= max) return names.join(', ');
  const rest = names.length - max;
  const more = lang === 'es' ? `+${rest} más` : `+${rest} more`;
  return `${names.slice(0, max).join(', ')} ${more}`;
}

/**
 * The hover card names EVERYONE (Miles, 2026-09-20: the card, not a peek at
 * three of them, is the desktop answer to "who reacted"). It is still capped,
 * because the card is `pointer-events: none` by construction — it must never
 * eat the click meant for the chip underneath it — so it can never be scrolled,
 * and an uncapped card would bury the conversation on a 40-reaction message.
 *
 * `truncated` is the card's cue to state the gesture that reaches the rest; the
 * hint's WORDS belong to the card, not here, so this stays a pure leaf.
 */
export const HOVER_NAMES_MAX = 8;

export function reactionHoverNames(
  entry: ReactionSummaryEntry,
  labelFor: (uid: string) => string,
  lang: 'en' | 'es',
  selfUid?: string | null,
): { text: string; truncated: boolean } {
  const text = reactionNamesPreview(entry, labelFor, lang, selfUid, HOVER_NAMES_MAX);
  const named = entry.uids.filter((uid) => !!(labelFor(uid) || '')).length;
  return { text, truncated: named > HOVER_NAMES_MAX };
}
